import { nanoid } from "nanoid";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { progressStore } from "./progress-store";
import { resolveToastDuration, ToastSeverity } from "./toast-duration";
import type {
  NoticeToastOptions,
  ProgressFailOptions,
  ProgressHandle,
  ProgressToastOptions,
  ToastMessage,
  ToastOptions,
} from "./toast-options";

// The extras are not on `ToastOptions`, so a feature cannot pass them without a cast.
type EnqueueOptions = ToastOptions & {
  /** Selects STICKY: never auto-hides and survives max-stack eviction. */
  sticky?: boolean;
  /** Fires once the toast has left the screen, whatever closed it. */
  onExited?: () => void;
  /** Overrides the dedupe rule below; `false` guarantees the toast is shown. */
  dedupe?: boolean;
};

const isFailure = (severity: ToastSeverity) => severity === "warning" || severity === "error";

const enqueueToast = (
  severity: ToastSeverity,
  message: ToastMessage,
  options: EnqueueOptions = {},
): SnackbarKey => {
  const { key, title, action, undo, sticky, testId, onExited, dedupe } = options;
  const { category, duration } = resolveToastDuration(severity, { action, undo, sticky });

  return enqueueSnackbar(message, {
    key,
    variant: severity,
    // The kit owns the dismiss clock (`useDismissTimer`), so notistack never starts one.
    // Only a sticky toast is persisted: that is what protects it from max-stack eviction,
    // and a timed toast should still be the first to go.
    persist: duration === null,
    autoHideDuration: null,
    // By key when given, otherwise by message for failures, so a burst of the same error
    // reads as one toast.
    preventDuplicate: dedupe ?? (key != null || isFailure(severity)),
    action,
    onExited,
    severity,
    category,
    duration,
    title,
    undo,
    testId,
  });
};

/**
 * The only toast transport. Lifecycle is chosen by the method plus at most one
 * named option (`undo`, `action`); call sites never pass milliseconds, positions
 * or styles. Durations live in `ToastDuration`.
 */
export const toast = {
  success: (message: ToastMessage, options?: ToastOptions): SnackbarKey =>
    enqueueToast("success", message, options),
  info: (message: ToastMessage, options?: ToastOptions): SnackbarKey =>
    enqueueToast("info", message, options),
  warning: (message: ToastMessage, options?: ToastOptions): SnackbarKey =>
    enqueueToast("warning", message, options),
  error: (message: ToastMessage, options?: ToastOptions): SnackbarKey =>
    enqueueToast("error", message, options),

  /**
   * An app-level notice with no other home, such as a build waiting to be applied.
   * The one toast without a timer: it never auto-hides and survives max-stack
   * eviction, so it needs a `key` for code to close it. It renders on the info
   * surface and carries no severity (see `NoticeToastOptions`).
   */
  notice: (message: ToastMessage, options: NoticeToastOptions): SnackbarKey =>
    enqueueToast("info", message, { ...options, sticky: true }),

  /**
   * Shows an un-dismissable progress toast and returns the handle that ends it.
   * Resolve it through `succeed` or `fail`, or `close` it from unmount cleanup;
   * nothing else removes it.
   */
  progress(
    message: ToastMessage,
    { key, value, testId }: ProgressToastOptions = {},
  ): ProgressHandle {
    const id: SnackbarKey = key ?? `toast-progress-${nanoid()}`;
    progressStore.set(id, { message, value });
    enqueueSnackbar(message, {
      key: id,
      variant: "progress",
      persist: true,
      testId,
      // The store entry outlives the close request so the exit animation keeps its last frame.
      onExited: () => progressStore.remove(id),
    });

    let settled = false;
    const finish = (
      severity: ToastSeverity,
      outcome: ToastMessage,
      options?: ToastOptions,
    ): Promise<void> => {
      if (settled) return Promise.resolve();
      settled = true;
      closeSnackbar(id);
      return new Promise<void>((resolve) => {
        // Never deduped: a duplicate that was dropped would never fire `onExited`,
        // and this promise would hang.
        enqueueToast(severity, outcome, { ...options, onExited: resolve, dedupe: false });
      });
    };

    return {
      update(patch) {
        if (!settled) progressStore.update(id, patch);
      },
      succeed: (outcome, options) => finish("success", outcome, options),
      fail: (outcome, { severity = "error", ...options }: ProgressFailOptions = {}) =>
        finish(severity, outcome, options),
      close() {
        if (settled) return;
        settled = true;
        closeSnackbar(id);
      },
    };
  },

  close(key: SnackbarKey): void {
    closeSnackbar(key);
  },
} as const;
